import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type StructureDetail, type StructureType } from '../lib/api';
import { useAuth } from '../lib/auth';
import { StructurePhotosCard, type PhotoDto } from './StructurePhotosCard';
import { ShareStructureModal } from './ShareStructureModal';
import { ProductTour, type TourStep } from '../lib/ProductTour';
import { castingFilterImageSrc, familyOf, filterImageSrc, parseFieldsJson, populateDiagram, structureDisplayName } from '../lib/structureReport';

/**
 * Phase K.5 — Step 5 (Structure Report). Layout mirrors the legacy
 * `/structurereport/view/{hashid}` screen: green style-number banner,
 * guest "Create A Free Account" CTA, three edit-step shortcuts, then a
 * two-column body with Ordering Info on the left and a structure-dimensions
 * panel + uploaded photos on the right.
 */
const STRUCTURE_TOUR_STEPS: TourStep[] = [
  {
    selector: '[data-tour="sstep1"]',
    intro: 'Share Or Submit: The fastest way to send your order to an ADS representative is by using the share option here.',
  },
  {
    selector: '[data-tour="sstep2"]',
    intro: 'Structure Information: You will need both the style number and sizing information to place your order. The share button above automatically includes this information with your request.',
  },
  {
    selector: '[data-tour="sstep3"]',
    intro: 'Download PDF: If you would like a record of this structure for later use, download a PDF here.',
  },
  {
    selector: '[data-tour="sstep4"]',
    intro: 'Save and Close Structure: Finally, save this report to get a quote later, or add this and other parts to a project folder to get all your quotes at once.',
  },
  {
    selector: '[data-tour="sstep5"]',
    intro: 'You can replay this tour at any time by clicking here.',
  },
];

// Equivalent to the legacy server-side `structuretour` flag: auto-show once
// per user (desktop only, matching the legacy non-mobile UA gate). Stored
// client-side to match the app's one-time-UI convention (cf. `fs.hometour`).
const STRUCTURETOUR_KEY = 'fs.structuretour';

function castingDimension(
  width: number | null,
  height: number | null,
  diameter: number | null,
): string | null {
  if (diameter != null && diameter > 0) return `${diameter}`;
  if (width != null && height != null && (width > 0 || height > 0)) return `${width} x ${height}`;
  return null;
}

export function StructureReportPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isGuest = !!user?.wasGuest;

  const detailQuery = useQuery({
    queryKey: ['structure', id],
    queryFn: () => api.get<StructureDetail>(`/api/v1/structures/${id}`),
    enabled: !!id,
  });

  const typesQuery = useQuery({
    queryKey: ['structure-types'],
    queryFn: () => api.get<StructureType[]>('/api/v1/structure-types'),
  });

  // Shares the cache key used by StructurePhotosCard so the "In Use" badge
  // reflects the actual photo count (legacy `@if ($structure->photos->count() > 0)`).
  const photosQuery = useQuery({
    queryKey: ['structure-photos', detailQuery.data?.id],
    queryFn: () => api.get<PhotoDto[]>(`/api/v1/structures/${detailQuery.data!.id}/photos`),
    enabled: detailQuery.data?.id != null,
  });

  const [shareOpen, setShareOpen] = useState(false);
  const [shareMode, setShareMode] = useState<'share' | 'quote'>('share');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [tourRunning, setTourRunning] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const svgRef = useRef<HTMLObjectElement>(null);
  const svgMobileRef = useRef<HTMLObjectElement>(null);

  // Deep-link from the "Request your quote" email button
  // (`/structurereport/{token}?directshare=1`): open the Share modal in quote
  // mode automatically, then strip the flag so a refresh doesn't reopen it.
  useEffect(() => {
    if (searchParams.get('directshare') !== '1') return;
    setShareMode('quote');
    setShareOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('directshare');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Auto-start the tour once for registered (non-guest) users on desktop,
  // mirroring the legacy `structuretour` flag + non-mobile UA gate.
  useEffect(() => {
    if (isGuest) return;
    if (localStorage.getItem(STRUCTURETOUR_KEY)) return;
    localStorage.setItem(STRUCTURETOUR_KEY, '1');
    if (window.matchMedia('(min-width: 992px)').matches) {
      setTourRunning(true);
    }
  }, [isGuest]);

  // Legacy parity (report.blade.php PopulateSVGs): the dimensions diagram is an
  // embedded SVG whose `<text id="{field}_output">` / `{field}_output_2` nodes
  // are filled with the saved dimensions. This only works when the SVG is
  // embedded via `<object>` (so its inner DOM is reachable), not an `<img>`.
  // Enabled fields are always written; extra outputs only when > 0.
  const writeReportOutputs = useCallback(() => {
    const detail = detailQuery.data;
    const type = typesQuery.data?.find((t) => t.id === detail?.structureTypeId) ?? null;
    if (!detail || !type) return;
    populateDiagram([svgRef.current, svgMobileRef.current], detail, type);
  }, [detailQuery.data, typesQuery.data]);

  useEffect(() => {
    writeReportOutputs();
  }, [writeReportOutputs]);

  if (detailQuery.isLoading) {
    return (
      <div className="container-fluid">
        <div className="row mt-lg-3 mt-1 justify-content-center">
          <div className="col-12 col-lg-6 text-center text-muted">Loading…</div>
        </div>
      </div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="container-fluid">
        <div className="row mt-lg-3 mt-1 justify-content-center">
          <div className="col-12 col-lg-6">
            <div className="alert alert-danger">Could not load structure {id}.</div>
            <Link to="/newstructure" className="btn btn-sm btn-outline-secondary">
              ← Start over
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const s = detailQuery.data;
  const displayName = structureDisplayName(s);
  const styleNumber = s.partNumber ?? s.foundryPartNumber ?? '—';
  const structureType = typesQuery.data?.find((t) => t.id === s.structureTypeId) ?? null;
  const family = familyOf(structureType);
  const extraOutputFields = parseFieldsJson(structureType?.extraOutputs);
  const enabledFieldDimensions = parseFieldsJson(structureType?.enabledFields);
  const isCasting = s.foundryPartId != null;
  const photoCount = photosQuery.data?.length ?? 0;
  // Casting-based structures have no structure_type, so their sample image is
  // derived from the foundry part's round-ness (legacy castingreport.blade.php).
  const filterImage = isCasting
    ? castingFilterImageSrc(s.foundryPartGrateSizeDiameter, s.permanent, s.oilFiltration)
    : filterImageSrc(structureType, s.permanent, s.oilFiltration);
  const castingImage = s.foundryPartAbbreviation
    ? `/images/CASTING/${s.foundryPartAbbreviation}.png`
    : null;
  const grateOpening = castingDimension(
    s.foundryPartGrateSizeWidth,
    s.foundryPartGrateSizeHeight,
    s.foundryPartGrateSizeDiameter,
  );
  const concreteOpening = castingDimension(
    s.foundryPartOpeningSizeWidth,
    s.foundryPartOpeningSizeHeight,
    s.foundryPartOpeningSizeDiameter,
  );

  const dimensionsTitle = s.nickname
    ? `Structure Dimensions For ${s.nickname}`
    : 'Structure Dimensions';

  const reportToken = s.hashId || id;
  const editDimsHref = `/newstructure/dimensions?id=${reportToken}&family=${family}&temp=${s.permanent ? 'false' : 'true'}&oil=${s.oilFiltration ? 'true' : 'false'}`;
  const editNotesHref = `/newstructure/notes?id=${reportToken}`;
  const editFilterHref = `/newstructure?id=${reportToken}&temp=${s.permanent ? 'false' : 'true'}&oil=${s.oilFiltration ? 'true' : 'false'}`;
  const editCastingHref = `/newstructure/casting?temp=${s.permanent ? 'false' : 'true'}&oil=${s.oilFiltration ? 'true' : 'false'}&id=${reportToken}${s.foundryId != null ? `&foundryId=${s.foundryId}` : ''}`;

  const drawingAbbr = structureType?.abbreviation ?? '';

  // Legacy parity (`structure/nopart.blade.php` via `lookuppart`): when the
  // casting matcher couldn't resolve a FlexStorm part (e.g. a curb opening
  // larger than the biggest Open Throat filter we stock) and no foundry part
  // was picked, the old tool showed a dedicated "unable to find a matching
  // part" screen instead of a report with a blank Style Number.
  if (s.partId == null && s.foundryPartId == null) {
    return (
      <div className="container-fluid">
        <div className="row mt-lg-3 mt-1 justify-content-center">
          <div className="col-12 col-xl-6 col-lg-8 text-center">
            <h1>
              We are sorry, but we were unable to find a matching part for that
              configuration.
            </h1>
            <p>
              It seems we are unable to find a part that matches the
              specifications you&rsquo;ve entered. Please contact us and let us
              know the details of the part you are looking for and any other
              requirements or specifications you have.
            </p>
            <div>
              <Link to="/" className="btn btn-sm btn-primary m-1">
                Return To Menu
              </Link>
              <Link to="/newstructure" className="btn btn-sm btn-primary m-1">
                Create New Structure
              </Link>
              <Link to={editDimsHref} className="btn btn-sm btn-primary m-1">
                Return to Structure Dimensions
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Mirrors the legacy `delete_structure` POST + redirect to the structure index.
  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.del<void>(`/api/v1/structures/${s.id}`);
      navigate('/admin/structures');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Could not delete structure');
      setDeleting(false);
    }
  };

  // "Close Structure" — legacy shows a confirmation modal for drafts with three
  // choices: Save Structure (undraft → structure list), Keep As Draft (home),
  // Delete Draft (delete confirm). Non-draft structures close straight to home.
  const handleCloseStructure = () => {
    if (s.isDraft) {
      setSaveError(null);
      setCloseOpen(true);
    } else {
      navigate('/');
    }
  };

  // "Save Structure" — clear the draft flag, then land on the structure list
  // (mirrors legacy `undraft` → view_structure_index redirect).
  const handleSaveStructure = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await api.post<void>(`/api/v1/structures/${s.id}/undraft`);
      navigate('/admin/structures');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save structure');
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid d-lg-block">
      {/* Guest upsell banner */}
      {isGuest && (
        <div className="row justify-content-center border p-3 mt-2 mx-0 d-print-none">
          <div className="col-12 text-center">
            Sign up to save this configuration, get instant access to previous structures, multi-structure projects, and more!
          </div>
          <div className="col-12 pt-2 text-center">
            <Link to="/register" className="btn btn-primary">
              Create A Free Account
            </Link>
          </div>
        </div>
      )}

      {/* Draft notice + Manage/Share action toolbar (registered users only).
          Mirrors the legacy `report.blade.php` desktop nav row that sits above
          the edit-step tabs. */}
      {!isGuest && (
        <>
          {s.isDraft && (
            <div className="alert alert-danger d-print-none" role="alert">
              This structure will be a draft in your structure list until added to a project, shared or saved.
            </div>
          )}

          <div className="d-none d-lg-block row mt-3 border-bottom mx-0">
            <ul className="nav flex-column text-center text-lg-start flex-lg-row mb-1 justify-content-evenly px-0">
              <li className="nav-item">
                <a
                  href="#"
                  className="nav-link link-success"
                  onClick={(e) => {
                    e.preventDefault();
                    setShareMode('quote');
                    setShareOpen(true);
                  }}
                >
                  <span className="d-md-block">
                    <img
                      src="/images/ICON/Icon_Clipboard_Checkmark_Small_Green.png"
                      alt=""
                      className="me-2 align-middle"
                      style={{ display: 'inline-block', height: 28, width: 'auto' }}
                      onError={(ev) => {
                        (ev.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    Request A Quote
                  </span>
                </a>
              </li>
              <li className="nav-item">
                <a
                  href="#"
                  data-tour="sstep1"
                  className="nav-link link-success"
                  onClick={(e) => {
                    e.preventDefault();
                    setShareMode('share');
                    setShareOpen(true);
                  }}
                >
                  <span className="d-md-block">
                    <img
                      src="/images/ICON/Icon_Share_Green.png"
                      alt=""
                      className="me-2 align-middle"
                      style={{ display: 'inline-block', height: 28, width: 'auto' }}
                      onError={(ev) => {
                        (ev.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    Share Or Submit
                  </span>
                </a>
              </li>
              <li className="nav-item">
                <Link to={`/structure/addtoproject/${id}`} className="nav-link link-success">
                  <span className="d-md-block">
                    <img
                      src="/images/ICON/Icon_Upload.png"
                      alt=""
                      className="me-2 align-middle"
                      style={{ display: 'inline-block', height: 28, width: 'auto' }}
                      onError={(ev) => {
                        (ev.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    Add To Project Folder
                  </span>
                </Link>
              </li>
              <li className="nav-item">
                <a
                  href={`/structure/pdf/${reportToken}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-tour="sstep3"
                  className="nav-link link-success"
                >
                  <span className="d-md-block">
                    <img
                      src="/images/ICON/Icon_Download_Green.png"
                      alt=""
                      className="me-2 align-middle"
                      style={{ display: 'inline-block', height: 28, width: 'auto' }}
                      onError={(ev) => {
                        (ev.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    Download Structure PDF
                  </span>
                </a>
              </li>
              <li className="nav-item">
                <a
                  href="#"
                  data-tour="sstep4"
                  className="nav-link link-success"
                  onClick={(e) => {
                    e.preventDefault();
                    handleCloseStructure();
                  }}
                >
                  <span className="d-md-block">
                    <img
                      src="/images/ICON/Icon_Save.png"
                      alt=""
                      className="me-2 align-middle"
                      style={{ display: 'inline-block', height: 28, width: 'auto' }}
                      onError={(ev) => {
                        (ev.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    Close Structure
                  </span>
                </a>
              </li>
              <li className="nav-item">
                <a
                  href="#"
                  className="nav-link link-success"
                  onClick={(e) => {
                    e.preventDefault();
                    setDeleteError(null);
                    setDeleteOpen(true);
                  }}
                >
                  <img
                    src="/images/ICON/Icon_Trash_Big.png"
                    alt=""
                    className="me-2 align-middle"
                    style={{ display: 'inline-block', height: 28, width: 'auto' }}
                    onError={(ev) => {
                      (ev.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  Delete Structure
                </a>
              </li>
            </ul>
          </div>
        </>
      )}

      {/* Edit-step shortcut tabs */}
      <div className="d-none d-lg-block row mt-3 border-bottom mx-0">
        <ul className="nav flex-column text-center text-lg-start flex-lg-row mb-1 justify-content-evenly px-0">
          <li className="nav-item">
            <Link to={editFilterHref} className="nav-link link-dark">
              <span className="d-md-inline-block">
                <img
                  src="/images/ICON/Icon_StructureType.png"
                  alt=""
                  className="me-2 align-middle"
                  style={{ display: 'inline-block', height: 28, width: 'auto' }}
                />
                Edit Filter Type
              </span>
            </Link>
          </li>
          <li className="nav-item">
            <Link to={isCasting ? editCastingHref : editDimsHref} className="nav-link link-dark">
              <span className="d-md-inline-block">
                <img
                  src={isCasting ? '/images/ICON/Icon_StructureType.png' : '/images/ICON/Icon_Dimensions.png'}
                  alt=""
                  className="me-2 align-middle"
                  style={{ display: 'inline-block', height: 28, width: 'auto' }}
                />
                {isCasting ? 'Edit Casting Number' : 'Edit Structure Dimensions'}
              </span>
            </Link>
          </li>
          <li className="nav-item">
            <Link to={editNotesHref} className="nav-link link-dark">
              <span className="d-md-inline-block">
                <img
                  src="/images/ICON/Icon_Report.png"
                  alt=""
                  className="me-2 align-middle"
                  style={{ display: 'inline-block', height: 28, width: 'auto' }}
                />
                Edit Additional Notes and Photos
              </span>
            </Link>
          </li>
        </ul>
      </div>

      <div className="tab-content pt-2">
        <div className="tab-pane show active">
          <div className="container-fluid mb-4 px-0">
            <div className="row px-2">
              {/* Left column — Structure Information */}
              <div className="col-12 col-md-6 col-lg-5 col-xl-4">
                <div className="row w-100 p-0 mx-0">
                  <div className="col-12 ms-1 d-flex align-items-center">
                    <i className="text-muted fs-1 mdi mdi-information-outline" aria-hidden="true" />
                    <span className="fs-4 text-muted ps-1"> Structure Information</span>
                  </div>
                </div>

                <div className="m-1 row border mb-3 pb-2" data-tour="sstep2">
                  <div className="col-12 d-md-flex d-grid justify-content-between py-2 border-bottom">
                    <span className="p-2 fw-bold">Ordering Info</span>
                  </div>
                  <div className="col-12 px-3">
                    <div className="py-2">
                      When ordering, please provide the information below or use the share buttons above. This info is required to supply the correct&nbsp;part.
                    </div>

                    <div className="pb-1 pt-2 border-top d-md-flex d-grid justify-content-between">
                      <span className="fw-bold">Style Number</span>
                      <span className="ms-md-2 fw-bold text-success">{styleNumber}</span>
                    </div>

                    {s.hangerKitTransitionPlatePartNumber && (
                      <div className="py-1 d-md-flex d-grid justify-content-between">
                        <span className="fw-bold">Hanger Kit / Transition Plate</span>
                        <span className="ms-md-2">{s.hangerKitTransitionPlatePartNumber}</span>
                      </div>
                    )}

                    {isCasting && (
                      <>
                        {s.foundryName && (
                          <div className="py-1 d-md-flex d-grid justify-content-between">
                            <span className="fw-bold">Foundry</span>
                            <span className="ms-md-2 fw-bold text-success">{s.foundryName}</span>
                          </div>
                        )}
                        {s.foundryPartNumber && (
                          <div className="py-1 d-md-flex d-grid justify-content-between">
                            <span className="fw-bold">Casting Number</span>
                            <span className="ms-md-2 fw-bold text-success">{s.foundryPartNumber}</span>
                          </div>
                        )}
                        {s.foundryPartLineNote && (
                          <div className="py-1 d-md-flex d-grid justify-content-between">
                            <span className="fw-bold">Line Note</span>
                            <span className="ms-md-2 fw-bold text-success">{s.foundryPartLineNote}</span>
                          </div>
                        )}
                        {grateOpening && (
                          <div className="py-1 d-md-flex d-grid justify-content-between">
                            <span className="fw-bold">
                              {s.foundryPartGrateSizeDiameter != null && s.foundryPartGrateSizeDiameter > 0
                                ? 'Diameter of Grate Opening'
                                : 'Dimensions of Grate Opening'}
                            </span>
                            <span className="ms-md-2 fw-bold text-success">{grateOpening} inches</span>
                          </div>
                        )}
                        {concreteOpening && (
                          <div className="py-1 d-md-flex d-grid justify-content-between">
                            <span className="fw-bold">Opening of Concrete</span>
                            <span className="ms-md-2 fw-bold text-success">{concreteOpening} inches</span>
                          </div>
                        )}
                      </>
                    )}

                    {extraOutputFields.map((f) => {
                      const v = s[f.key];
                      if (typeof v !== 'number' || v <= 0) return null;
                      return (
                        <div key={String(f.key)} className="py-1 d-md-flex d-grid justify-content-between">
                          <span className="fw-bold">{f.label}</span>
                          <span className="ms-md-2">{v} Inches</span>
                        </div>
                      );
                    })}

                    {enabledFieldDimensions.map((f) => {
                      const v = s[f.key];
                      if (typeof v !== 'number' || v <= 0) return null;
                      return (
                        <div key={String(f.key)} className="py-1 d-md-flex d-grid justify-content-between">
                          <span className="fw-bold">{f.label}</span>
                          <span className="ms-md-2 fw-bold text-success">{v} Inches</span>
                        </div>
                      );
                    })}

                    {/* Category/Subcategory are precast-only. Castings have no
                        structure_type, and the legacy castingreport.blade.php
                        deliberately omitted these rows for them. */}
                    {!isCasting && (
                      <>
                        <div className="pb-1 mt-1 pt-2 border-top d-md-flex d-grid justify-content-between">
                          <span className="fw-bold">Category</span>
                          <span>{s.structureTypeName ?? '—'}</span>
                        </div>

                        {structureType && structureType.family !== structureType.name && (
                          <div className="py-1 d-md-flex d-grid justify-content-between">
                            <span className="fw-bold">Subcategory</span>
                            <span>{structureType.family}</span>
                          </div>
                        )}
                      </>
                    )}

                    {s.grateStyleName && (
                      <div className="py-1 d-md-flex d-grid justify-content-between">
                        <span className="fw-bold">Grate Style</span>
                        <span>{s.grateStyleName}</span>
                      </div>
                    )}

                    <div className="py-1 d-md-flex d-grid justify-content-between">
                      <span className="fw-bold">Filter Type</span>
                      <span>{s.permanent ? 'Permanent' : 'Temporary'}</span>
                    </div>

                    {s.permanent && (
                      <div className="py-1 d-md-flex d-grid justify-content-between">
                        <span className="fw-bold">Oil Removal</span>
                        <span>{s.oilFiltration ? 'Yes' : 'No'}</span>
                      </div>
                    )}

                    <div className="px-0 py-1 d-flex">
                      <span>In Use:&nbsp;</span>
                      <a href="#requirements" className="text-decoration-none">
                        <span className="badge ms-2 px-2 rounded-pill bg-success">Requirements</span>
                      </a>
                      {photoCount > 0 && (
                        <a href="#photos" className="text-decoration-none">
                          <span className="badge ms-2 px-2 rounded-pill bg-success">Photos</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {filterImage && (
                  <div className="m-1 row border">
                    <img
                      src={filterImage}
                      alt="FlexStorm filter"
                      className="img-fluid p-0"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {s.structureNotes && (
                  <div className="m-1 row border">
                    <span className="p-2 fw-bold">Additional Requirements or Notes</span>
                    <div className="pb-2">{s.structureNotes}</div>
                  </div>
                )}
              </div>

              {/* Right column — Structure Dimensions */}
              <div className="col-12 col-md-6 col-lg-7 col-xl-8">
                <div className="row me-0 w-100 p-0 mx-0">
                  <div className="col-12 w-100 ms-1 d-flex align-items-center">
                    <i className="text-muted fs-1 mdi mdi-information-outline" aria-hidden="true" />
                    <span className="fs-4 text-muted ps-1 flex-grow-1"> {dimensionsTitle}</span>
                    {!isGuest && (
                      <span className="text-end d-print-none">
                        <a
                          href="#"
                          data-tour="sstep5"
                          className="link-success text-nowrap"
                          onClick={(e) => {
                            e.preventDefault();
                            setTourRunning(true);
                          }}
                        >
                          Tour{' '}
                          <img
                            src="/images/ICON/Icon_Question_Filled_Green.png"
                            alt=""
                            className="d-inline-block align-middle"
                            onError={(ev) => {
                              (ev.currentTarget as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </a>
                      </span>
                    )}
                  </div>
                </div>
                <div className="m-1 row border">
                  <div className="col-12">
                    {castingImage ? (
                      <img
                        src={castingImage}
                        alt="Casting illustration"
                        className="img-fluid my-4 mx-auto d-block"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : drawingAbbr ? (
                      <>
                        <object
                          ref={svgRef}
                          type="image/svg+xml"
                          className="d-none d-lg-block print-svg-desktop p-1 m-1 p-xl-4 m-xl-4 img-fluid mx-auto"
                          data={`/images/DRAWINGS/${drawingAbbr}.svg`}
                          aria-label={dimensionsTitle}
                          onLoad={writeReportOutputs}
                        />
                        <object
                          ref={svgMobileRef}
                          type="image/svg+xml"
                          className="d-block d-lg-none print-svg-mobile p-1 m-1 img-fluid mx-auto"
                          data={`/images/DRAWINGS/${drawingAbbr}-Mobile.svg`}
                          aria-label={dimensionsTitle}
                          onLoad={writeReportOutputs}
                        />
                      </>
                    ) : (
                      <p className="text-center text-muted py-4">No diagram available</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Photos */}
              <div className="col-12" id="photos">
                <div className="m-1">
                  <StructurePhotosCard structureId={s.id} />
                </div>
              </div>

              {/* Action buttons. On desktop these live in the top toolbar
                  (mirroring legacy); this block is the mobile fallback so
                  small screens retain the same actions. Guests are excluded. */}
              {!isGuest && (
                <div className="col-12 row d-lg-none d-block mx-0 d-print-none">
                  <button
                    type="button"
                    className="btn btn-success m-2"
                    onClick={() => {
                      setShareMode('share');
                      setShareOpen(true);
                    }}
                  >
                    Share Or Submit
                  </button>
                  <button
                    type="button"
                    className="btn btn-success m-2"
                    onClick={() => {
                      setShareMode('quote');
                      setShareOpen(true);
                    }}
                  >
                    Request A Quote
                  </button>
                  <Link
                    to={`/structure/addtoproject/${id}`}
                    className="btn btn-success m-2"
                  >
                    Add To Project Folder
                  </Link>
                  <a
                    href={`/structure/pdf/${reportToken}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-success m-2"
                  >
                    Download Structure PDF
                  </a>
                  <button
                    type="button"
                    className="btn btn-success m-2"
                    onClick={handleCloseStructure}
                  >
                    Close Structure
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger m-2"
                    onClick={() => {
                      setDeleteError(null);
                      setDeleteOpen(true);
                    }}
                  >
                    Delete Structure
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {shareOpen && (
        <ShareStructureModal
          structureId={s.id}
          ownerEmail={user?.email ?? null}
          mode={shareMode}
          onClose={() => setShareOpen(false)}
        />
      )}

      {deleteOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !deleting && setDeleteOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-structure-title"
        >
          <div
            className="w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn-close p-2"
                aria-label="Close"
                onClick={() => !deleting && setDeleteOpen(false)}
              />
            </div>
            <div className="modal-body text-center px-4 pb-4">
              <h1 id="delete-structure-title">Delete Structure</h1>
              <p>
                {s.isDraft
                  ? `The structure "${displayName}" will be removed from your account.`
                  : `The structure "${displayName}" will be removed from all projects it has been added to and will not be viewable by anyone who has been emailed it.`}
              </p>
              {deleteError && <p className="text-danger">{deleteError}</p>}
              <div className="d-inline text-center">
                <button
                  type="button"
                  className="btn btn-secondary m-2"
                  disabled={deleting}
                  onClick={() => setDeleteOpen(false)}
                >
                  Do Not Delete
                </button>
                <button
                  type="button"
                  className="btn btn-danger m-2"
                  disabled={deleting}
                  onClick={handleDelete}
                >
                  {deleting ? 'Deleting…' : 'DELETE'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {closeOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !saving && setCloseOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="close-structure-title"
        >
          <div
            className="w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn-close p-2"
                aria-label="Close"
                onClick={() => !saving && setCloseOpen(false)}
              />
            </div>
            <div className="modal-body text-center px-4 pb-4">
              <h1 id="close-structure-title">Close Structure</h1>
              <p>
                This structure will be a draft in your structure list until added to a
                project, shared or saved.
              </p>
              {saveError && <p className="text-danger">{saveError}</p>}
              <div className="d-inline text-center">
                <button
                  type="button"
                  className="btn btn-secondary m-2"
                  disabled={saving}
                  onClick={handleSaveStructure}
                >
                  {saving ? 'Saving…' : 'Save Structure'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary m-2"
                  disabled={saving}
                  onClick={() => navigate('/')}
                >
                  Keep As Draft
                </button>
                <button
                  type="button"
                  className="btn btn-danger m-2"
                  disabled={saving}
                  onClick={() => {
                    setCloseOpen(false);
                    setDeleteError(null);
                    setDeleteOpen(true);
                  }}
                >
                  Delete Draft
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ProductTour
        steps={STRUCTURE_TOUR_STEPS}
        run={tourRunning}
        onClose={() => setTourRunning(false)}
      />
    </div>
  );
}
